import type { ChannelConfig } from "@x402/evm";
import type {
  BatchSettlementClientContext,
  ClientChannelStorage,
} from "@x402/evm/batch-settlement/client";
import { computeChannelId } from "@x402/evm/batch-settlement/client";
import { keccak256, stringToHex } from "viem";

type StringStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type BatchChannelIdentity = {
  channelId: string;
  channelConfig: ChannelConfig;
};

export const DEFAULT_BATCH_CHANNEL_SALT = keccak256(
  stringToHex("bizx402:batch:default:v1"),
);

export function batchChannelSalt(channel?: BatchChannelIdentity): `0x${string}` {
  return channel?.channelConfig.salt || DEFAULT_BATCH_CHANNEL_SALT;
}

export function assertBatchChannelIdentity(
  channel: BatchChannelIdentity | undefined,
  channelConfig: ChannelConfig,
  network: string,
): void {
  if (!channel) return;
  const computedChannelID = computeChannelId(channelConfig, network);
  if (computedChannelID.toLowerCase() !== channel.channelId.toLowerCase()) {
    throw new Error("充值通道参数与当前余额不一致，已停止钱包签名，请刷新后重试。");
  }
}

type BatchCumulativeState = Pick<
  BatchSettlementClientContext,
  "chargedCumulativeAmount" | "totalClaimed" | "signedMaxClaimable"
>;

export function batchTopUpCumulativeAmount(
  onchainTotalClaimed: string,
  ...states: Array<Partial<BatchCumulativeState> | undefined>
): string {
  let maximum = BigInt(onchainTotalClaimed);
  for (const state of states) {
    if (!state) continue;
    for (const field of ["chargedCumulativeAmount", "totalClaimed", "signedMaxClaimable"] as const) {
      const candidate = state[field];
      if (candidate !== undefined && BigInt(candidate) > maximum) maximum = BigInt(candidate);
    }
  }
  return maximum.toString();
}

const DECIMAL_FIELDS = [
  "chargedCumulativeAmount",
  "balance",
  "totalClaimed",
  "signedMaxClaimable",
] as const;

function validContext(value: unknown): BatchSettlementClientContext | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const source = value as Record<string, unknown>;
  const context: BatchSettlementClientContext = {};
  for (const field of DECIMAL_FIELDS) {
    const candidate = source[field];
    if (candidate === undefined) continue;
    if (typeof candidate !== "string" || !/^\d+$/.test(candidate)) return undefined;
    context[field] = candidate;
  }
  if (source.signature !== undefined) {
    if (typeof source.signature !== "string" || !/^0x[0-9a-fA-F]+$/.test(source.signature)) {
      return undefined;
    }
    context.signature = source.signature as `0x${string}`;
  }
  return context;
}

export class BrowserBatchChannelStorage implements ClientChannelStorage {
  constructor(
    private readonly storage: StringStorage,
    private readonly namespace = "bizx402-batch-channel",
  ) {}

  async get(key: string): Promise<BatchSettlementClientContext | undefined> {
    const storageKey = this.key(key);
    const saved = this.storage.getItem(storageKey);
    if (!saved) return undefined;
    try {
      const context = validContext(JSON.parse(saved));
      if (context) return context;
    } catch {
      // Invalid browser state is discarded and recovered from the server.
    }
    this.storage.removeItem(storageKey);
    return undefined;
  }

  async set(key: string, context: BatchSettlementClientContext): Promise<void> {
    const safe = validContext(context);
    if (!safe) throw new Error("invalid batch channel state");
    this.storage.setItem(this.key(key), JSON.stringify(safe));
  }

  async delete(key: string): Promise<void> {
    this.storage.removeItem(this.key(key));
  }

  private key(channelID: string): string {
    return `${this.namespace}:${channelID.trim().toLowerCase()}`;
  }
}
